// ProjectIcons — one hand-drawn line mark per project, each saying what the
// thing actually does (assertion brackets for a test runner, a fan-out for a
// gateway, a gate for human-in-the-loop). 24×24 grid, 1.4 stroke, currentColor
// so the cards can tint them stone-400 → stone-900 on hover.

type IconProps = { className?: string };

function Svg({ className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  );
}

// mcp-jest — an assertion passing inside test brackets.
export function IconMcpJest({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M7.6 4H6a2.4 2.4 0 0 0-2.4 2.4v11.2A2.4 2.4 0 0 0 6 20h1.6" />
      <path d="M16.4 4H18a2.4 2.4 0 0 1 2.4 2.4v11.2A2.4 2.4 0 0 1 18 20h-1.6" />
      <path d="m8.7 12.3 2.3 2.4 4.3-5.4" />
    </Svg>
  );
}

// aa-kit — a toolbox of agent parts.
export function IconAaKit({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="2.8" y="7.8" width="18.4" height="12.4" rx="2.4" />
      <path d="M9 7.8V6.4A1.9 1.9 0 0 1 10.9 4.5h2.2A1.9 1.9 0 0 1 15 6.4v1.4" />
      <path d="M2.8 12.6h6.4m5.6 0h6.4" />
      <rect x="9.2" y="10.8" width="5.6" height="3.6" rx="1.2" />
    </Svg>
  );
}

// Engram — a memory core with the retain → recall → reflect loop around it.
export function IconEngram({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M20.2 12a8.2 8.2 0 1 1-2.7-6.1" />
      <path d="M20.6 3.4v3.9h-3.9" />
      <circle cx="12" cy="12" r="3.4" />
    </Svg>
  );
}

// Approval Protocol — a run halted at a gate until a human signs off.
export function IconApproval({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M2.4 12h4.2" />
      <rect x="6.6" y="5.6" width="10.8" height="12.8" rx="2.6" />
      <path d="m9.3 12 2 2.1 3.5-4.4" />
      <path d="M17.4 12h4.2" />
    </Svg>
  );
}

// Freeport — one call fanning out to many providers.
export function IconFreeport({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M2.2 12h3.6" />
      <circle cx="9" cy="12" r="3.2" />
      <path d="M11.8 10.4 19.8 6M12.2 12h7.6M11.8 13.6 19.8 18" />
    </Svg>
  );
}

// Grain — an agent definition as structured, declared rows.
export function IconGrain({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="4.5" y="3" width="15" height="18" rx="2" />
      <circle cx="8.2" cy="8" r="0.9" />
      <circle cx="8.2" cy="12" r="0.9" />
      <circle cx="8.2" cy="16" r="0.9" />
      <path d="M10.8 8h5M10.8 12h4.4M10.8 16h5.4" />
    </Svg>
  );
}

// pixelpi — a real browser driven from a prompt.
export function IconPixelpi({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 8.2h18" />
      <circle cx="6.1" cy="6.1" r="0.7" />
      <circle cx="8.5" cy="6.1" r="0.7" />
      <path d="m7.6 11.8 2.2 2.1-2.2 2.1" />
      <path d="M11.6 16h4.8" />
    </Svg>
  );
}

// Tongue — spoken language, coming back as a waveform.
export function IconTongue({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M4 6.5a2.5 2.5 0 0 1 2.5-2.5h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4 3.6V16h-.5A2.5 2.5 0 0 1 4 13.5z" />
      <path d="M8.8 8.4v3.6M12 6.6v7.2M15.2 8.4v3.6" />
    </Svg>
  );
}

// Lockbox — credentials, kept shut.
export function IconLockbox({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="4" y="9.5" width="16" height="11" rx="2" />
      <path d="M8 9.5V7a4 4 0 0 1 8 0v2.5" />
      <circle cx="12" cy="14" r="1.3" />
      <path d="M12 15.3v2.2" />
    </Svg>
  );
}

// Plum Health Benefits — cover, in the insurance sense.
export function IconHealthShield({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M12 3.2 20 6v6.2c0 4.2-3.2 7.4-8 8.6-4.8-1.2-8-4.4-8-8.6V6z" />
      <path d="M12 9v6M9 12h6" />
    </Svg>
  );
}
